import { motion } from "framer-motion";
import { useRef, useState } from "react";
import ScrambleText from "./ScrambleText";

const HEADLINERS = [
  {
    name: "HÉCTOR OAKS",
    role: "HEADLINER · SATURDAY",
    origin: "MADRID · KHIDI",
    img: "https://images.unsplash.com/photo-1723209937791-564ee3e40259?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    name: "ANETHA",
    role: "HEADLINER · SUNDAY",
    origin: "PARIS · MAMA TOLD YA",
    img: "https://images.unsplash.com/photo-1768885512777-4ca25206756f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
];

const SUPPORT = [
  { name: "SETAOC MASS", origin: "UK · SK_ELEVEN" },
  { name: "CLERIC", origin: "UK · CLERGY" },
  { name: "BLASHA & ALLATT", origin: "UK · MORD" },
  { name: "TEMUDO", origin: "PORTUGAL · ARTS" },
  { name: "BEC", origin: "UK · R&S" },
  { name: "KR!Z", origin: "BELGIUM · TOKEN" },
];

const LOCAL = [
  { name: "BLOT!", origin: "NEW DELHI" },
  { name: "ZOKHUMA", origin: "MUMBAI" },
  { name: "_RHL", origin: "BENGALURU" },
  { name: "KUMAIL", origin: "MUMBAI" },
];

/**
 * SpotlightCard — cursor-tracked radial mask reveals the COLOR image,
 * while the full image stays grayscale underneath.
 */
function SpotlightCard({ a, i }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50, active: false });
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
      active: true,
    });
  };
  const onLeave = () => setPos((p) => ({ ...p, active: false }));
  const mask = `radial-gradient(circle 220px at ${pos.x}% ${pos.y}%, #000 0%, #000 55%, transparent 75%)`;

  return (
    <motion.article
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: i * 0.1 }}
      data-testid={`lineup-headliner-${i}`}
      className="group relative aspect-[4/5] overflow-hidden bg-[#1C1917] cursor-none"
    >
      {/* Grayscale base image (always visible) */}
      <img
        src={a.img}
        alt={a.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover grayscale opacity-70 scale-105"
      />
      {/* Full-color image masked by cursor radial */}
      <img
        src={a.img}
        alt=""
        aria-hidden
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 scale-105"
        style={{
          WebkitMaskImage: mask,
          maskImage: mask,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          opacity: pos.active ? 1 : 0,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-[#0C0A09]/30 to-transparent" />
      <div className="absolute top-6 left-6 flex items-center gap-3">
        <span className="tech-tag">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22D3EE]" />
          HDLN · {String(i + 1).padStart(2, "0")}
        </span>
        <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#F59E0B]">
          {a.role}
        </span>
      </div>
      <div className="absolute bottom-6 left-6 right-6">
        <h3 className="font-display text-3xl lg:text-5xl font-black uppercase tracking-tight text-[#FFF7ED]">
          <ScrambleText text={a.name} trigger="hover" duration={800} />
        </h3>
        <p className="mt-2 text-[11px] font-semibold tracking-[0.24em] uppercase text-stone-300">
          {a.origin}
        </p>
      </div>
      <div className="absolute top-6 right-6 h-10 w-10 grid place-items-center border border-white/30 text-white group-hover:bg-[#EA580C] group-hover:border-[#EA580C] transition-colors">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M7 17L17 7M17 7H9M17 7V15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="square"
          />
        </svg>
      </div>
    </motion.article>
  );
}

export default function Lineup() {
  return (
    <section
      id="lineup"
      data-testid="lineup-section"
      className="relative bg-[#0C0A09] py-24 lg:py-36 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.32em] uppercase text-[#F59E0B] mb-4 flex items-center gap-3">
              <span className="tech-tag">
                <span className="eq" aria-hidden>
                  <span /><span /><span /><span /><span />
                </span>
                01 / LINEUP
              </span>
            </p>
            <h2 className="font-display text-4xl lg:text-6xl font-black tracking-tight uppercase leading-[0.95] text-[#FFF7ED]">
              Names{" "}
              <span className="font-serif-italic text-[#EA580C] font-normal">
                that travel
              </span>
              <br />
              far from the mainstream.
            </h2>
          </div>
          <p className="max-w-sm text-stone-400 text-sm lg:text-base readout">
            <span className="text-[#22D3EE]">// </span>Twelve artists, three
            nights, one horizon. Hover each card to see in full colour.
          </p>
        </div>

        {/* Headliners */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {HEADLINERS.map((a, i) => (
            <SpotlightCard key={a.name} a={a} i={i} />
          ))}
        </div>

        {/* Support tier — hover scramble with cyan catalog numbers */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#292524]">
          {SUPPORT.map((a, i) => (
            <motion.div
              key={a.name}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              data-testid={`lineup-support-${i}`}
              className="bg-[#0C0A09] p-6 hover:bg-[#1C1917] transition-colors duration-500 group"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="readout text-[10px] tracking-[0.2em] text-[#22D3EE] group-hover:text-[#FFF7ED] transition-colors">
                  SB/BLR·{String(i + 3).padStart(2, "0")}
                </span>
                <span className="eq opacity-40 group-hover:opacity-100 transition-opacity" aria-hidden style={{ height: "14px" }}>
                  <span /><span /><span /><span /><span />
                </span>
              </div>
              <div className="font-display text-lg lg:text-xl font-bold text-[#FFF7ED] tracking-tight">
                <ScrambleText text={a.name} trigger="hover" duration={700} />
              </div>
              <div className="mt-1 text-[10px] tracking-[0.22em] uppercase text-stone-500">
                {a.origin}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Local acts */}
        <div className="mt-10 flex flex-wrap gap-3">
          <span className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#EA580C] self-center mr-2">
            India focus
          </span>
          {LOCAL.map((a, i) => (
            <motion.span
              key={a.name}
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              data-testid={`lineup-local-${i}`}
              className="inline-flex items-center gap-3 border border-[#292524] px-5 py-3 hover:border-[#EA580C] transition-colors"
            >
              <span className="font-display font-bold text-[#FFF7ED] text-sm">
                <ScrambleText text={a.name} trigger="hover" duration={600} />
              </span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-stone-500">
                {a.origin}
              </span>
            </motion.span>
          ))}
        </div>
      </div>
    </section>
  );
}
